import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import type { Dispatch } from '@tmapy/redux';
import { useMessage } from '@tmapy/intl';
import { unaccentAndLower } from '@tmapy/beetlejuice-utils';

import type { MultiSelectProps } from '@tmapy/style-guide';
import {
  BtnBar,
  Form,
  FormItem,
  FormRow,
  Input,
  MultiSelect,
  CtaBtn,
  SecondaryBtn,
  StickyFooter,
} from '@tmapy/style-guide';

import type { OmittedFormItemProps } from '../../types';
import { useInput, useMultiSelect } from '../../hooks';
import type { InputProps } from '../../hooks/useInput';
import type { UserRoleResponse } from '../../openapi/types';
import { UsersListQuery } from '../../openapi/operations/usersList';
import { useUserRolesListResource } from '../../openapi/operations/userRolesList';
import { setUserListFilter } from '../../redux/filter/actions';

const renderRoleItem = (item: UserRoleResponse) => item.label;

export const msg = defineMessages({
  submitBtn: {
    id: 'userFilter.submitBtn.label',
    defaultMessage: 'Filtrovat',
  },
  userFullName: {
    id: 'userFilter.userFullName.label',
    defaultMessage: 'Fulltext',
  },
  role: {
    id: 'userFilter.role.label',
    defaultMessage: 'Role',
  },
  clearBtn: {
    id: 'userFilter.clearBtn.label',
    defaultMessage: 'Zrušit filtr',
  },
});

type UserFiltersProps = {
  filters: UsersListQuery;
  dispatch: Dispatch;
};

export const UserFilter: React.FC<UserFiltersProps> = ({ filters, dispatch }) => {
  const formatMessage = useMessage();

  const userFullName = useInput<InputProps>({
    name: 'search',
    initialValue: filters.search,
    label: formatMessage(msg.userFullName),
  });

  const availableRoles = useUserRolesListResource({}, true);

  const prepareRoleItems = useCallback(
    async (val: string) => {
      const results = availableRoles.data?.results;
      if (results) {
        if (!val) return results;
        const normalizedValue = unaccentAndLower(val);
        return results.filter((role) => unaccentAndLower(role.name).includes(normalizedValue));
      }
      return null;
    },
    [availableRoles],
  );

  const roleInitialValues = useMemo(() => {
    const results = availableRoles.data?.results;
    if (results && filters.roles) {
      return results.filter(
        ({ name }) => filters.roles!.includes(name as any), // v OpenAPI neni type
      );
    }

    return [];
  }, [availableRoles, filters.roles]);

  const roles = useMultiSelect<
    MultiSelectProps<UserRoleResponse> | OmittedFormItemProps,
    UserRoleResponse
  >({
    name: 'roles',
    label: formatMessage(msg.role),
    initialValue: roleInitialValues,
    prepareItems: prepareRoleItems,
    renderValue: (item) => (item.label ? item.label : ''),
    renderItem: renderRoleItem,
  });

  const filterClearScope = [userFullName, roles];

  function handleClearBtnClick() {
    filterClearScope.forEach((formItem) => formItem?.clear?.());
    dispatch(setUserListFilter({}));
  }

  function handleSubmit() {
    dispatch(
      setUserListFilter({
        [userFullName.name!]: userFullName.value,
        [roles.name!]: roles.values.map((item) => item.name),
      }),
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      <div className='sg-u-vs-2'>
        <FormRow>
          <FormItem {...userFullName}>
            <Input {...userFullName} />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem {...roles}>
            <MultiSelect {...roles} />
          </FormItem>
        </FormRow>
      </div>
      <StickyFooter>
        <BtnBar stretch>
          <CtaBtn type='submit'>
            <FormattedMessage {...msg.submitBtn} />
          </CtaBtn>
          <SecondaryBtn isDanger onClick={handleClearBtnClick}>
            <FormattedMessage {...msg.clearBtn} />
          </SecondaryBtn>
        </BtnBar>
      </StickyFooter>
    </Form>
  );
};
